// The constraint types, one per card (see docs/architecture/review-constraints.md).
// A constraint is plain data; several owners may constrain the same card and
// the registry combines them, the strictest winning. Every type has a default
// that means "no constraint". Names are literal unions, with readonly arrays
// where code iterates them; identifiers are type-only brands.

// ---- names ----------------------------------------------------------------

/** What a scope belongs to: a board tab of the review, or a played game. */
export type ConstraintScopeKind = 'reviewTab' | 'play';
export const CONSTRAINT_SCOPE_KINDS = ['reviewTab', 'play'] as const satisfies readonly ConstraintScopeKind[];

/** The constraint types, one per card. */
export type ConstraintType =
  | 'controls'
  | 'nextMove'
  | 'scoresheetVisibility'
  | 'bestMovesVisibility'
  | 'boardArrowsVisibility'
  | 'scoreBarVisibility';
export const CONSTRAINT_TYPES = [
  'controls',
  'nextMove',
  'scoresheetVisibility',
  'bestMovesVisibility',
  'boardArrowsVisibility',
  'scoreBarVisibility',
] as const satisfies readonly ConstraintType[];

/** What may happen to the next move played on the board: none, any legal move, or played and judged by the owner, who may take it back. */
export type NextMoveMode = 'none' | 'any' | 'judged';
export const NEXT_MOVE_MODES = ['none', 'any', 'judged'] as const satisfies readonly NextMoveMode[];

// ---- identifiers ------------------------------------------------------------

/** Identifies a scope within its kind, e.g. a review tab's id. */
export type ConstraintScopeId = string & { readonly __brand: 'ConstraintScopeId' };
/** Identifies who registered constraints, e.g. a plugin's name. */
export type OwnerId = string & { readonly __brand: 'OwnerId' };
/** What the reader sees for a registration, e.g. "Puzzle". */
export type ConstraintLabel = string & { readonly __brand: 'ConstraintLabel' };
/** A longer description of a registration, e.g. a puzzle's prompt. */
export type ConstraintDetail = string & { readonly __brand: 'ConstraintDetail' };

function nonEmpty(value: string, what: string): string {
  if (typeof value !== 'string' || value.trim() === '') throw new Error(`${what} must be a non-empty string`);
  return value;
}
export const constraintScopeId = (value: string): ConstraintScopeId => nonEmpty(value, 'ConstraintScopeId') as ConstraintScopeId;
export const ownerId = (value: string): OwnerId => nonEmpty(value, 'OwnerId') as OwnerId;
export const constraintLabel = (value: string): ConstraintLabel => nonEmpty(value, 'ConstraintLabel') as ConstraintLabel;
export const constraintDetail = (value: string): ConstraintDetail => nonEmpty(value, 'ConstraintDetail') as ConstraintDetail;

/** Where constraints apply: one board tab of the review, one played game. */
export interface ConstraintScope {
  kind: ConstraintScopeKind;
  id: ConstraintScopeId;
}

/** The registry's key for a scope. */
export type ConstraintScopeKey = string & { readonly __brand: 'ConstraintScopeKey' };
export const constraintScopeKey = (scope: ConstraintScope): ConstraintScopeKey => `${scope.kind}:${scope.id}` as ConstraintScopeKey;

// ---- the types --------------------------------------------------------------

/** The navigation controls: back and forward (buttons, arrow keys) and scoresheet clicks. */
export interface ControlsConstraint {
  type: 'controls';
  /** Back and start, the left and up keys. */
  back: boolean;
  /** Forward and end, the right and down keys. */
  forward: boolean;
  /**
   * Plies the controls may reach, both ends included. While set, the controls
   * look along the board's path within it (start goes to `from`, end to `to`)
   * instead of moving through the game.
   */
  range?: { from: number; to: number };
  /** Clicking a move in the scoresheet. */
  scoresheetClicks: boolean;
}

/** Whether the reader may play the next move on the board. */
export interface NextMoveConstraint {
  type: 'nextMove';
  allow: NextMoveMode;
}

/** What the scoresheet shows. */
export interface ScoresheetVisibilityConstraint {
  type: 'scoresheetVisibility';
  /** Moves from this index of the game's moves (0-based) on are hidden; absent: none. */
  hideFrom?: number;
}

/** Whether the best-moves card shows its lines (engine lines and the theory list). */
export interface BestMovesVisibilityConstraint {
  type: 'bestMovesVisibility';
  visible: boolean;
}

/** Whether the board draws the engine's arrows. */
export interface BoardArrowsVisibilityConstraint {
  type: 'boardArrowsVisibility';
  visible: boolean;
}

/** Whether the score bar shows the evaluation. */
export interface ScoreBarVisibilityConstraint {
  type: 'scoreBarVisibility';
  visible: boolean;
}

export type Constraint =
  | ControlsConstraint
  | NextMoveConstraint
  | ScoresheetVisibilityConstraint
  | BestMovesVisibilityConstraint
  | BoardArrowsVisibilityConstraint
  | ScoreBarVisibilityConstraint;

/** The constraint of a given type. */
export type ConstraintOf<T extends ConstraintType> = Extract<Constraint, { type: T }>;

/** Each type's value with nothing registered. */
export const DEFAULTS: { readonly [T in ConstraintType]: ConstraintOf<T> } = {
  controls: { type: 'controls', back: true, forward: true, scoresheetClicks: true },
  nextMove: { type: 'nextMove', allow: 'any' },
  scoresheetVisibility: { type: 'scoresheetVisibility' },
  bestMovesVisibility: { type: 'bestMovesVisibility', visible: true },
  boardArrowsVisibility: { type: 'boardArrowsVisibility', visible: true },
  scoreBarVisibility: { type: 'scoreBarVisibility', visible: true },
};

const MOVE_STRICTNESS: Readonly<Record<NextMoveMode, number>> = { any: 0, judged: 1, none: 2 };

function minOptional(a: number | undefined, b: number | undefined): number | undefined {
  if (a === undefined) return b;
  if (b === undefined) return a;
  return Math.min(a, b);
}

/** Combine two constraints of one type, the strictest winning. */
export function combinePair<T extends ConstraintType>(a: ConstraintOf<T>, b: ConstraintOf<T>): ConstraintOf<T> {
  const x = a as Constraint;
  switch (x.type) {
    case 'controls': {
      const y = b as ControlsConstraint;
      let range = x.range ?? y.range;
      if (x.range && y.range) {
        const from = Math.max(x.range.from, y.range.from);
        const to = Math.min(x.range.to, y.range.to);
        // Disjoint ranges leave nowhere to go: hold at the later start.
        range = from <= to ? { from, to } : { from, to: from };
      }
      const out: ControlsConstraint = { type: 'controls', back: x.back && y.back, forward: x.forward && y.forward, scoresheetClicks: x.scoresheetClicks && y.scoresheetClicks };
      if (range) out.range = range;
      return out as ConstraintOf<T>;
    }
    case 'nextMove': {
      const y = b as NextMoveConstraint;
      return (MOVE_STRICTNESS[x.allow] >= MOVE_STRICTNESS[y.allow] ? x : y) as ConstraintOf<T>;
    }
    case 'scoresheetVisibility': {
      const y = b as ScoresheetVisibilityConstraint;
      const hideFrom = minOptional(x.hideFrom, y.hideFrom);
      const out: ScoresheetVisibilityConstraint = { type: 'scoresheetVisibility' };
      if (hideFrom !== undefined) out.hideFrom = hideFrom;
      return out as ConstraintOf<T>;
    }
    case 'bestMovesVisibility':
    case 'boardArrowsVisibility':
    case 'scoreBarVisibility': {
      const y = b as { visible: boolean };
      return { type: x.type, visible: x.visible && y.visible } as ConstraintOf<T>;
    }
  }
}

/** Combine every constraint of one type; the default when there are none. */
export function combine<T extends ConstraintType>(type: T, constraints: readonly Constraint[]): ConstraintOf<T> {
  let out: ConstraintOf<T> = DEFAULTS[type] as ConstraintOf<T>;
  for (const c of constraints) if (c.type === type) out = combinePair(out, c as ConstraintOf<T>);
  return out;
}
